import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { User, Clock, CheckCircle, XCircle, Eye, Phone, MapPin, Briefcase, Shield } from 'lucide-react';
import { useAuthStore } from '../../state/auth';
import { useTranslation } from '../../i18n';

interface PendingUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  requestedRole: 'GUARD' | 'SUPERVISOR';
  submittedAt: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  
  // Additional application data
  address: string;
  city: string;
  state: string;
  zipCode: string;
  currentEmployer: string;
  position: string;
  yearsExperience: string;
  hasSecurityExperience: boolean;
  securityExperienceDetails?: string;
  hasLicenses: boolean;
  licenseDetails?: string;
  availableShifts: string[];
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelation: string;
  reference1Name: string;
  reference1Phone: string;
  reference1Relation: string;
  reference2Name?: string;
  reference2Phone?: string;
  reference2Relation?: string;
  additionalInfo?: string;
}

interface EmployeePersonalInfo {
  name: string;
  phone_number: string;
}

// Demo data
const demoUsers: PendingUser[] = [
  {
    id: '1',
    firstName: 'Michael',
    lastName: 'Johnson',
    email: 'michael.johnson@email.com',
    phone: '(555) 123-4567',
    requestedRole: 'GUARD',
    submittedAt: '2025-01-02T10:30:00Z',
    status: 'PENDING',
    address: '123 Main Street',
    city: 'Atlanta',
    state: 'GA',
    zipCode: '30309',
    currentEmployer: 'SecureGuard Inc',
    position: 'Security Officer',
    yearsExperience: '4-5',
    hasSecurityExperience: true,
    securityExperienceDetails: 'Worked at various retail locations and office buildings. Experienced with access control systems and incident reporting.',
    hasLicenses: true,
    licenseDetails: 'Georgia Security Officer License (expires 2026), CPR certified',
    availableShifts: ['Day Shift (6am-2pm)', 'Evening Shift (2pm-10pm)', 'Weekends'],
    emergencyContactName: 'Sarah Johnson',
    emergencyContactPhone: '(555) 987-6543',
    emergencyContactRelation: 'Spouse',
    reference1Name: 'David Smith',
    reference1Phone: '(555) 555-0123',
    reference1Relation: 'Former Supervisor at SecureGuard',
    reference2Name: 'Lisa Williams',
    reference2Phone: '(555) 555-0456',
    reference2Relation: 'HR Manager at SecureGuard',
    additionalInfo: 'Very motivated to transition to university security. Available to start immediately.'
  },
  {
    id: '2',
    firstName: 'Amanda',
    lastName: 'Rodriguez',
    email: 'amanda.rodriguez@email.com',
    phone: '(555) 234-5678',
    requestedRole: 'SUPERVISOR',
    submittedAt: '2025-01-01T14:15:00Z',
    status: 'PENDING',
    address: '456 Oak Avenue',
    city: 'Decatur',
    state: 'GA',
    zipCode: '30030',
    currentEmployer: 'Allied Universal',
    position: 'Senior Security Supervisor',
    yearsExperience: '10+',
    hasSecurityExperience: true,
    securityExperienceDetails: 'Over 12 years in security management. Supervised teams of 15+ officers across multiple sites. Expert in scheduling, training, and incident management.',
    hasLicenses: true,
    licenseDetails: 'Georgia Armed Security License, Security Management Certification, First Aid/CPR/AED Instructor',
    availableShifts: ['Day Shift (6am-2pm)', 'Evening Shift (2pm-10pm)', 'Weekends', 'On-call'],
    emergencyContactName: 'Carlos Rodriguez',
    emergencyContactPhone: '(555) 876-5432',
    emergencyContactRelation: 'Spouse',
    reference1Name: 'Robert Chen',
    reference1Phone: '(555) 555-0789',
    reference1Relation: 'Regional Manager at Allied Universal',
    reference2Name: 'Jennifer Davis',
    reference2Phone: '(555) 555-0321',
    reference2Relation: 'Client Manager at Previous Site',
    additionalInfo: 'Looking for growth opportunity in academic environment. Strong background in technology integration and staff development.'
  },
  {
    id: '3',
    firstName: 'James',
    lastName: 'Wilson',
    email: 'james.wilson@email.com',
    phone: '(555) 345-6789',
    requestedRole: 'GUARD',
    submittedAt: '2024-12-30T09:45:00Z',
    status: 'PENDING',
    address: '789 Pine Street',
    city: 'Marietta',
    state: 'GA',
    zipCode: '30060',
    currentEmployer: 'Fresh Market',
    position: 'Loss Prevention Associate',
    yearsExperience: '2-3',
    hasSecurityExperience: true,
    securityExperienceDetails: 'Retail loss prevention for 3 years. Experienced with surveillance systems, apprehension procedures, and report writing.',
    hasLicenses: false,
    licenseDetails: '',
    availableShifts: ['Night Shift (10pm-6am)', 'Weekends', 'Holidays'],
    emergencyContactName: 'Mary Wilson',
    emergencyContactPhone: '(555) 765-4321',
    emergencyContactRelation: 'Mother',
    reference1Name: 'Tom Anderson',
    reference1Phone: '(555) 555-0147',
    reference1Relation: 'Loss Prevention Manager',
    additionalInfo: 'Recent college graduate looking to start career in security. Very reliable and detail-oriented.'
  }
];

const employeesPersonalInfo: EmployeePersonalInfo[] = [
  { name: "Michael Johnson", phone_number: "(555) 123-4567" },
  { name: "Amanda Rodriguez", phone_number: "(555) 234-5678" },
  { name: "James Wilson", phone_number: "(555) 345-6789" }
];

export const UserManagementPage: React.FC = () => {
  const { t, lang } = useTranslation();
  const { user: currentUser } = useAuthStore();

  const statusLabel = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PENDING':
        return t('status.pending', 'Pending');
      case 'APPROVED':
        return t('status.approved', 'Approved');
      case 'REJECTED':
        return t('users.statusRejected', 'Rejected');
      default:
        return status;
    }
  };

  const roleLabel = (role: string) => {
    switch (role.toUpperCase()) {
      case 'GUARD':
        return t('role.guard', 'Security Guard');
      case 'SUPERVISOR':
        return t('role.supervisor', 'Supervisor');
      default:
        return role;
    }
  };

  const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat(lang, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    }).format(new Date(value));

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(lang, {
      year: 'numeric', month: 'short', day: 'numeric',
    }).format(new Date(value));

  // Role-based access control
  if (!currentUser || !['ADMIN', 'DIRECTOR'].includes(currentUser.role)) {
    return <Navigate to="/supervisor" replace />;
  }

  const [users, setUsers] = useState<PendingUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED'>('ALL');

  // Load applications from localStorage and merge with demo data
  useEffect(() => {
    const loadApplications = () => {
      const storedApplications = JSON.parse(localStorage.getItem("pendingApplications") || "[]");
      // Combine demo data with real applications
      const allApplications = [...demoUsers, ...storedApplications];
      setUsers(allApplications);
    };

    loadApplications();
    
    // Listen for storage changes to update in real-time
    const handleStorageChange = () => {
      loadApplications();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const filteredUsers = users.filter(user => {
    if (filter === 'ALL') return true;
    return user.status === filter;
  });

  const handleApprove = (userId: string) => {
    // Update state
    setUsers(prev => prev.map(user => 
      user.id === userId ? { ...user, status: 'APPROVED' as const } : user
    ));
    if (selectedUser?.id === userId) {
      setSelectedUser(prev => prev ? { ...prev, status: 'APPROVED' as const } : null);
    }
    
    // Update localStorage for non-demo applications
    const storedApplications = JSON.parse(localStorage.getItem("pendingApplications") || "[]");
    const updatedApplications = storedApplications.map((app: any) => 
      app.id === userId ? { ...app, status: 'APPROVED' } : app
    );
    localStorage.setItem("pendingApplications", JSON.stringify(updatedApplications));
  };

  const handleReject = (userId: string) => {
    // Update state
    setUsers(prev => prev.map(user => 
      user.id === userId ? { ...user, status: 'REJECTED' as const } : user
    ));
    if (selectedUser?.id === userId) {
      setSelectedUser(prev => prev ? { ...prev, status: 'REJECTED' as const } : null);
    }
    
    // Update localStorage for non-demo applications
    const storedApplications = JSON.parse(localStorage.getItem("pendingApplications") || "[]");
    const updatedApplications = storedApplications.map((app: any) => 
      app.id === userId ? { ...app, status: 'REJECTED' } : app
    );
    localStorage.setItem("pendingApplications", JSON.stringify(updatedApplications));
  };

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PENDING':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'APPROVED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-md";
    switch (status.toUpperCase()) {
      case 'PENDING':
        return `${baseClasses} bg-amber-100 text-amber-800`;
      case 'APPROVED':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'REJECTED':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return baseClasses;
    }
  };

  if (selectedUser) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setSelectedUser(null)}
            className="text-gray-600 hover:text-gray-800"
          >
            ← {t('users.backToApplications', 'Back to Applications')}
          </button>
          <h1 className="text-2xl font-bold text-gray-800">
            {t('users.applicationReview', 'Application Review:')} {selectedUser.firstName} {selectedUser.lastName}
          </h1>
          <span className={getStatusBadge(selectedUser.status)}>
            {statusLabel(selectedUser.status)}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Application Details */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('users.applicationDetails', 'Application details')}
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Detail label={t('common.name', 'Name')} value={`${selectedUser.firstName} ${selectedUser.lastName}`} />
                <Detail label={t('users.email', 'Email')} value={selectedUser.email} />
                <Detail label={t('users.phone', 'Phone')} value={selectedUser.phone} />
                <Detail label={t('users.requestedRole', 'Requested role')} value={roleLabel(selectedUser.requestedRole)} />
                <Detail label={t('users.dateAdded', 'Date added')} value={formatDateTime(selectedUser.submittedAt)} />
                <Detail label={t('common.status', 'Status')} value={statusLabel(selectedUser.status)} />
              </div>
            </div>
            {(() => {
              const entry = employeesPersonalInfo.find((info) => info.name === `${selectedUser.firstName} ${selectedUser.lastName}`);
              if (!entry) return null;
              return (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('users.directoryMatch', 'Directory match')}</h3>
                  <p className="text-sm text-gray-600">{t('users.directoryFound', 'Found in employees_personal_info table.')}</p>
                  <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                    <Detail label={t('users.directoryName', 'Directory name')} value={entry.name} />
                    <Detail label={t('users.phoneNumber', 'Phone number')} value={entry.phone_number} />
                  </div>
                </div>
              );
            })()}
          </div>
          {/* Action Panel */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('users.applicationStatus', 'Application Status')}</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {getStatusIcon(selectedUser.status)}
                  <span className={getStatusBadge(selectedUser.status)}>
                    {statusLabel(selectedUser.status)}
                  </span>
                </div>

                <div className="text-sm text-gray-600">
                  <p>{t('users.submitted', 'Submitted:')} {formatDate(selectedUser.submittedAt)}</p>
                </div>

                {selectedUser.status === 'PENDING' && (
                  <div className="space-y-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleApprove(selectedUser.id)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {t('users.approveApplication', 'Approve Application')}
                    </button>
                    
                    <button
                      onClick={() => handleReject(selectedUser.id)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      {t('users.rejectApplication', 'Reject Application')}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-medium text-amber-800 mb-2">{t('users.nextSteps', 'Next Steps')}</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• {t('users.stepVerifyReferences', 'Verify references by phone')}</li>
                <li>• {t('users.stepCheckLicense', 'Check license/certification validity')}</li>
                <li>• {t('users.stepBackgroundCheck', 'Initiate background check')}</li>
                <li>• {t('users.stepScheduleInterview', 'Schedule interview if approved')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{t('users.title')}</h1>
          <p className="text-gray-600">{t('users.subtitle')}</p>
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="ALL">{t('users.filterAll', 'All Applications')}</option>
            <option value="PENDING">{t('users.filterPending', 'Pending Review')}</option>
            <option value="APPROVED">{t('status.approved', 'Approved')}</option>
            <option value="REJECTED">{t('users.statusRejected', 'Rejected')}</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('users.colApplicant', 'Applicant')}</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('users.colRole', 'Role')}</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('users.dateAdded', 'Date added')}</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.status', 'Status')}</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      <div className="text-sm text-gray-500">{user.phone}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {roleLabel(user.requestedRole)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDateTime(user.submittedAt)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(user.status)}
                      <span className={getStatusBadge(user.status)}>{statusLabel(user.status)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium flex gap-3">
                    <button
                      onClick={() => setSelectedUser(user)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      {t('users.review', 'Review')}
                    </button>
                    {user.status === 'PENDING' && (
                      <>
                        <button
                          onClick={() => handleApprove(user.id)}
                          className="text-green-600 hover:text-green-900"
                        >
                          {t('users.approve', 'Approve')}
                        </button>
                        <button
                          onClick={() => handleReject(user.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t('users.reject', 'Reject')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t('users.noApplications', 'No applications found')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
      <p className="text-sm text-gray-900">{value ?? "—"}</p>
    </div>
  );
}
